import React, { useState } from 'react';
import Navigation from '../components/layout/Navigation';
import SEO from '../components/SEO';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar, Mail, Send, Check, Clock, ArrowRight, Home, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    childGrade: '',
    message: '',
    _hp_field: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const grades = [
    { value: 'k', label: 'Kindergarten / Grande Section' },
    { value: '1', label: 'Grade 1 / CP' },
    { value: '2', label: 'Grade 2 / CE1' },
    { value: '3', label: 'Grade 3 / CE2' },
    { value: '4', label: 'Grade 4 / CM1' },
    { value: '5', label: 'Grade 5 / CM2' },
    { value: '6', label: 'Grade 6 / 6ème' },
    { value: '7', label: 'Grade 7 / 5ème' },
    { value: '8', label: 'Grade 8 / 4ème' },
    { value: '9', label: 'Grade 9 / 3ème' },
    { value: '10', label: 'Grade 10 / 2nde' },
    { value: '11', label: 'Grade 11 / 1ère' },
    { value: '12', label: 'Grade 12 / Terminale' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${BACKEND_URL}/api/leads`, {
        ...formData,
        language,
        source: 'contact_form',
      });
      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        childGrade: '',
        message: '',
        _hp_field: '',
      });
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-white">
      <SEO path="/contact" />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-12 bg-[#E7EAEF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb / Back to home */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mb-6 flex items-center gap-2"
          >
            <Link
              to="/"
              data-testid="contact-back-home"
              className="inline-flex items-center gap-2 text-[#2D78F6] hover:text-[#14274B] transition-colors text-sm font-medium"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              <Home className="w-4 h-4" />
              {language === 'fr' ? 'Accueil' : 'Home'}
            </Link>
            <ChevronRight className="w-3 h-3 text-[#475569]/50" />
            <span className="text-[#475569] text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>Contact</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-[#14274B] leading-tight tracking-tight mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.contact.heroTitle}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-lg text-[#475569] max-w-2xl"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {t.contact.heroSubtitle}
          </motion.p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-[#2D78F6]/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#2D78F6]" />
                </div>
                <h2
                  className="text-2xl font-semibold text-[#14274B]"
                  style={{ fontFamily: 'Gabarito, sans-serif' }}
                >
                  {t.contact.form.title}
                </h2>
              </div>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-[#66CA9D]/10 p-8 text-center"
                >
                  <div className="w-16 h-16 bg-[#66CA9D] mx-auto mb-4 flex items-center justify-center">
                    <Check className="w-8 h-8 text-white" />
                  </div>
                  <h3
                    className="text-xl font-semibold text-[#14274B] mb-2"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {language === 'fr' ? 'Merci!' : 'Thank you!'}
                  </h3>
                  <p
                    className="text-[#475569]"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {language === 'fr'
                      ? 'Nous vous contacterons bientôt.'
                      : 'We will be in touch soon.'}
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot field - hidden from users, catches bots */}
                  <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                    <input
                      type="text"
                      name="_hp_field"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData._hp_field}
                      onChange={(e) => setFormData({ ...formData, _hp_field: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <Label
                        htmlFor="firstName"
                        className="text-[#14274B] font-medium"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        {t.contact.form.firstName}
                      </Label>
                      <Input
                        id="firstName"
                        data-testid="contact-firstname"
                        value={formData.firstName}
                        onChange={(e) =>
                          setFormData({ ...formData, firstName: e.target.value })
                        }
                        required
                        className="mt-2 bg-[#E7EAEF] border-0 focus:ring-2 focus:ring-[#2D78F6]"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      />
                    </div>
                    <div>
                      <Label
                        htmlFor="lastName"
                        className="text-[#14274B] font-medium"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        {t.contact.form.lastName}
                      </Label>
                      <Input
                        id="lastName"
                        data-testid="contact-lastname"
                        value={formData.lastName}
                        onChange={(e) =>
                          setFormData({ ...formData, lastName: e.target.value })
                        }
                        required
                        className="mt-2 bg-[#E7EAEF] border-0 focus:ring-2 focus:ring-[#2D78F6]"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <Label
                        htmlFor="email"
                        className="text-[#14274B] font-medium"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        {t.contact.form.email}
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        required
                        className="mt-2 bg-[#E7EAEF] border-0 focus:ring-2 focus:ring-[#2D78F6]"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      />
                    </div>
                    <div>
                      <Label
                        htmlFor="phone"
                        className="text-[#14274B] font-medium"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        {t.contact.form.phone}
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="contact-phone"
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                        className="mt-2 bg-[#E7EAEF] border-0 focus:ring-2 focus:ring-[#2D78F6]"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      />
                    </div>
                  </div>

                  <div>
                    <Label
                      htmlFor="childGrade"
                      className="text-[#14274B] font-medium"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      {t.contact.form.childGrade}
                    </Label>
                    <Select
                      value={formData.childGrade}
                      onValueChange={(value) =>
                        setFormData({ ...formData, childGrade: value })
                      }
                    >
                      <SelectTrigger
                        data-testid="contact-grade"
                        className="mt-2 bg-[#E7EAEF] border-0"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        <SelectValue placeholder={language === 'fr' ? 'Sélectionner' : 'Select'} />
                      </SelectTrigger>
                      <SelectContent>
                        {grades.map((grade) => (
                          <SelectItem key={grade.value} value={grade.value}>
                            {grade.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label
                      htmlFor="message"
                      className="text-[#14274B] font-medium"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      {t.contact.form.message}
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-message"
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      rows={4}
                      className="mt-2 bg-[#E7EAEF] border-0 focus:ring-2 focus:ring-[#2D78F6]"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    />
                  </div>

                  {error && (
                    <p className="text-[#EA5658] text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {error}
                    </p>
                  )}

                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#2D78F6] text-white hover:bg-[#14274B] py-6"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin">⏳</span>
                        {language === 'fr' ? 'Envoi...' : 'Sending...'}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        {t.contact.form.submit}
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="space-y-8"
            >
              {/* Email */}
              <div className="bg-[#E7EAEF] p-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#2D78F6]/10 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-[#2D78F6]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#14274B]" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                      Email
                    </h3>
                    <a href="mailto:info@ofalycee.fr" className="text-[#2D78F6] hover:underline" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      info@ofalycee.fr
                    </a>
                  </div>
                </div>
              </div>

              {/* Response time */}
              <div className="bg-[#E7EAEF] p-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#66CA9D]/10 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-[#66CA9D]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#14274B]" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                      {language === 'fr' ? 'Délai de réponse' : 'Response time'}
                    </h3>
                    <p className="text-[#475569] text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {language === 'fr' ? 'Nous répondons sous 48 heures ouvrées' : 'We respond within 48 business hours'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Social / Useful links */}
              <div className="bg-[#14274B] p-8">
                <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                  {language === 'fr' ? 'Liens utiles' : 'Useful links'}
                </h3>
                <ul className="space-y-3">
                  <li>
                    <Link to="/programs" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      <ArrowRight className="w-4 h-4" />
                      {language === 'fr' ? 'Découvrir nos programmes' : 'Discover our programs'}
                    </Link>
                  </li>
                  <li>
                    <Link to="/admissions" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      <ArrowRight className="w-4 h-4" />
                      {language === 'fr' ? 'Processus d\'admission' : 'Admission process'}
                    </Link>
                  </li>
                  <li>
                    <Link to="/tuition" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      <ArrowRight className="w-4 h-4" />
                      {language === 'fr' ? 'Frais de scolarité' : 'Tuition fees'}
                    </Link>
                  </li>
                  <li>
                    <Link to="/returning-families" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      <ArrowRight className="w-4 h-4" />
                      {language === 'fr' ? 'Quel parcours pour votre enfant ?' : 'Which track for your child?'}
                    </Link>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
